import { Button, Flex, Span } from "@goodfill/ui-components";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserAuthDataBalanceAccount } from "../../../backendApi/amplifyGraphQl/CustomGetUserData/UserData";
import { route } from "../../../../routes";
import { canModifyOrder } from "../../canModifyOrder";
import { Info } from "../../../Info";
import { OrderKillOrFillButton } from "../../OrderKillOrFillButton";
import { CreateOrderStatistics } from "../form/CreateOrderStatistics";
import { OrderFormState } from "../state/OrderFormState";
import { OrderTicketDetailEntity } from "@goodfill/domain-entities";
import { CorporateBondId, OrderTicketId, OrderDirection, TimeInForce, asAmount } from "@goodfill/domain-properties";
import {
  BondPriceComponent,
  ExpirationDateTimeComponent,
  FaceAmountComponent,
  TimeInForceComponent,
  YieldComponent,
} from "@goodfill/domain-properties-components";
import { LOCAL_TIMEZONE } from "@goodfill/domain-properties-components/src/localTimezone";
import { canKillOrFillOrder } from "../../canKillOrFillOrder";
import { calculateYield } from "@goodfill/domain-math";
import { TIMEZONE_NEY_YORK } from "../../../../locale/timezone";

export interface OrderFormResult {
  state: OrderFormState;
  orderTicket: OrderTicketDetailEntity;
}

export const OrderReceipt = (props: {
  result: OrderFormResult;
  moneyBalanceAccount: UserAuthDataBalanceAccount;
  showCalculatedInfo: boolean;
}) => {
  const navigate = useNavigate();

  const onClickFillOrKillSuccess = useCallback(() => {
    navigate(route.dashboard());
  }, [navigate]);

  const onClickModify = useCallback(
    (bondId: CorporateBondId, orderId: OrderTicketId) => () => {
      navigate(route.orderModify(bondId, orderId));
    },
    [navigate],
  );

  const state = props.result.state;
  const orderId = props.result.orderTicket.id;
  const orderDirection = state.i.orderDirection;
  const pricedBond = state.i.pricedBond;
  const orderTicket = props.result.orderTicket;
  const preFeePrice = props.result.orderTicket.bondPrice;
  const preFeeYield = calculateYield(
    TIMEZONE_NEY_YORK,
    pricedBond.couponPayment,
    pricedBond.couponFrequency,
    props.result.orderTicket.bondPrice,
    pricedBond.maturityDate,
    props.result.state.i.settlementDate,
  );

  const currency = props.moneyBalanceAccount.currency;
  if (currency === null) {
    return <>Error: missing currency for moneyBalanceAccount</>;
  }

  const prevailingMarketPrice = orderDirection === OrderDirection.Buy ? pricedBond.askPrice : pricedBond.bidPrice;

  return (
    <>
      <Flex direction="rowCol" gap={12} justify="between">
        <Flex direction="col" gap={2}>
          <Info label="Face amount" justify="end">
            <FaceAmountComponent value={state.s.faceAmount.amount} />
          </Info>
          <Info label="Order execution" justify="end">
            <TimeInForceComponent value={state.s.timeInForce} />
          </Info>
          <Info label="Order Price Pre-Fee" justify="end">
            <BondPriceComponent value={preFeePrice} />
          </Info>
          <Info label="Order Yield Pre-Fee" justify="end">
            <YieldComponent value={preFeeYield} />
          </Info>
          <Info label="Order Price Post-Fee" justify="end">
            <BondPriceComponent value={state.s.customLoadedPrice} />
          </Info>
          <Info label="Order Yield Post-Fee" justify="end">
            <YieldComponent value={state.s.customLoadedYield} />
          </Info>
        </Flex>

        <Flex direction="col" gap={2} justify="end" width={96}>
          {state.i.orderDirection === OrderDirection.Buy ? (
            <Span red={true} wrap={true}>
              Settlement Amount of funds will be reserved until order is filled. If the order has not been executed in
              the market, it may be canceled by selecting &quot;Fill or Kill&quot; below.
            </Span>
          ) : null}
          {state.s.expirationDate !== null ? (
            <>
              <Info label="Order expiration" justify="end">
                <ExpirationDateTimeComponent
                  value={state.s.expirationDate}
                  timezone={TIMEZONE_NEY_YORK}
                  showTimezone={true}
                />
              </Info>
              <Info label="" justify="end">
                <Span size="sm" neutral={true}>
                  <ExpirationDateTimeComponent
                    value={state.s.expirationDate}
                    timezone={LOCAL_TIMEZONE}
                    showTimezone={true}
                  />
                </Span>
              </Info>
            </>
          ) : null}
        </Flex>
      </Flex>
      <CreateOrderStatistics
        showCalculatedInfo={props.showCalculatedInfo}
        currency={currency}
        accruedInterest={state.s.timeInForce !== TimeInForce.CustomFuture ? state.c.accruedInterest : null}
        tradeDate={state.s.timeInForce !== TimeInForce.CustomFuture ? state.i.tradeDate : null}
        settlementDate={state.s.timeInForce !== TimeInForce.CustomFuture ? state.i.settlementDate : null}
        settlementValue={state.s.timeInForce !== TimeInForce.CustomFuture ? state.settlementAmount : null}
        transactionCostAmount={state.c.transactionCostAmount}
        transactionCostRateUpfront={state.c.transactionCostRateUpfront}
        prevailingMarketPrice={prevailingMarketPrice}
        actualInvestment={asAmount(state.s.faceAmount.amount.mul(state.s.customLoadedPrice))}
        orderId={orderId}
      />
      <Flex direction="row" justify="between" verticalMargin={2}>
        <Span size="2xl" font="semibold" uppercase={true} data-testid="order-confirmation" data-orderid={orderId}>
          {orderDirection === OrderDirection.Buy ? "buy" : "sell"} order confirmation
        </Span>
        <Flex direction="row" gap={6} items="center">
          <Flex width={32}>
            {canKillOrFillOrder(props.result.orderTicket) ? (
              <OrderKillOrFillButton orderId={orderId} onSuccess={onClickFillOrKillSuccess} />
            ) : null}
          </Flex>

          {canModifyOrder(orderTicket) ? (
            <Flex width={32}>
              <Button
                fill
                size="small"
                uppercase={true}
                primary={true}
                onClick={onClickModify(pricedBond.bondIdCusip, orderId)}
              >
                Modify
              </Button>
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </>
  );
};
